import { icon } from "@fortawesome/fontawesome-svg-core";
import { faVideoPlus } from "@fortawesome/pro-solid-svg-icons/faVideoPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor } from "@tinymce/tinymce-react";
import { PropTypes } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import MentionsEditor from "~/components/forms/HtmlEditors/MentionsEditor";
import GlobalContainer from "~/components/global_container";
import AssistModal from "~/components/modals/AiAssist/Modal";
import {
  IMAGES_UPLOAD_HANDLER,
  TASK_CAMPAIGN_EDITOR_TOOLBAR,
  TASK_EDITOR_PLUGINS,
  TASK_EDITOR_TOOLBAR,
  TASK_EDITOR_TOOLBAR_WITHOUT_VARIABLES,
  TINYMCE_SCRIPT_PATH,
  TINYMCE_SCROLLABLE_UI_OPTIONS,
} from "~/constants/TinyMCE";
import EditorHelpers from "~/helpers/editor_helpers";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { initWebSpellChecker } from "~/lib/web_spell_checker";

const TaskEditor = ({
  id = "",
  value = "",
  className = "",
  placeholder = "Enter text or insert / drop images ...",
  onChange = () => false,
  leadId = null,
  isCampaignStep = false,
  spellCheck = true,
  linksToInsert = [],
  clearLinksToInsert = () => false,
  replaceValue = null,
  clearReplaceValue = () => false,
  enableVariables = true,
  includeOwnerVariables = false,
  includeAlertVariables = true,
}) => {
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [hasCheckedFeature, setHasCheckedFeature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(value || "");
  const [showAssistModal, setShowAssistModal] = useState(false);
  const editorRef = useRef(null);
  const editorKey = [
    isCampaignStep ? "campaign" : "task",
    includeOwnerVariables || isCampaignStep ? "owner" : "no-owner",
    includeAlertVariables ? "alert" : "no-alert",
  ].join("-");
  let toolbar = TASK_EDITOR_TOOLBAR_WITHOUT_VARIABLES;
  if (isCampaignStep && enableVariables) {
    toolbar = TASK_CAMPAIGN_EDITOR_TOOLBAR;
  } else if (enableVariables) {
    toolbar = TASK_EDITOR_TOOLBAR;
  }

  const init = {
    placeholder,
    cache_suffix: "?v=7.9.1",
    license_key: "gpl",
    ...TINYMCE_SCROLLABLE_UI_OPTIONS,
    plugins: TASK_EDITOR_PLUGINS,
    toolbar,
    toolbar_mode: "wrap",
    branding: false,
    menubar: false,
    file_picker_types: "image",
    min_height: 350,
    relative_urls: false,
    remove_script_host: false,
    document_base_url: Rails.baseUrl,
    forced_root_block: "div",
    entity_encoding: "named",
    content_style: "body { font-family: Arial; font-size: 13px; }",
    paste_as_text: false,
    contextmenu: false,
    paste_remove_styles_if_webkit: false,
    statusbar: true,
    images_upload_handler: IMAGES_UPLOAD_HANDLER,
    setup: (editor) => {
      editor.ui.registry.addIcon("videoIcon", icon(faVideoPlus).html[0]);

      editor.ui.registry.addButton("videos", {
        icon: "videoIcon",
        tooltip: "Insert Video",
        onAction: async () => {
          const { mountVideosModal } = await import(
            /* webpackChunkName: "videos-modal" */ "~/components/modals/videos/VideosModal"
          );
          mountVideosModal({
            modalClass: "modal modal-overlay",
            dialogClass: "modal-dialog modal-lg",
            editor,
          });
        },
      });

      if (enableVariables) {
        EditorHelpers.addVariablesMenu(editor, {
          includeSender: false,
          includeOwner: includeOwnerVariables || isCampaignStep,
          includeAlertVariables,
        });
      }

      editor.ui.registry.addButton("ai-assist", {
        icon: "ai",
        tooltip: "AI Assist",
        onAction: () => setShowAssistModal(true),
      });

      EditorHelpers.addVisitLink(editor);
    },
  };

  useEffect(() => {
    const handlePosthogReady = () => {
      const isAccessAllowed = checkFeatureFlag(
        process.env.SHAKAPACKER_PUBLIC_ALLOW_MENTIONS_FLAG,
      );
      setIsFeatureEnabled(isAccessAllowed);
      setHasCheckedFeature(true);
    };

    if (window.isPosthogInitialized) {
      handlePosthogReady();
    } else {
      window.addEventListener("posthogReady", handlePosthogReady);
    }

    return () => {
      window.removeEventListener("posthogReady", handlePosthogReady);
    };
  }, []);

  useEffect(() => {
    if (replaceValue != null) {
      setContent(replaceValue);
      clearReplaceValue();
    }
  }, [replaceValue, clearReplaceValue]);

  useEffect(() => {
    if (linksToInsert?.length) {
      let newContent = content;
      linksToInsert.forEach((link) => {
        const { text, href } = link;
        const linkHtml = `<br/><a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>&nbsp;`;

        newContent += linkHtml;
      });
      setContent(newContent);
      clearLinksToInsert();
    }
  }, [linksToInsert, clearLinksToInsert]);

  useEffect(() => {
    initSpellCheckerForEditor();
  }, [spellCheck]);

  const initSpellCheckerForEditor = () => {
    const editor = editorRef.current;

    if (spellCheck && editor) {
      initWebSpellChecker(editor.iframeElement);
    }
  };

  const handleEditorChange = (_value, editor) => {
    const newContent = editor.getContent({ format: "html" });
    setContent(newContent);
    onChange(newContent);
  };

  const handleOnInit = (_event, editor) => {
    setLoading(false);

    if (value) {
      setContent(value);
      editor.setContent(value, { format: "html" });
    }

    editorRef.current = editor;
    initSpellCheckerForEditor();
  };

  return (
    <div className={`${className} position-relative`}>
      {loading && (
        <div className="d-flex justify-content-center align-items-center position-absolute w-100 h-100 bg-white z-5">
          <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />{" "}
          Loading
        </div>
      )}

      {isCampaignStep || (hasCheckedFeature && !isFeatureEnabled) ? (
        <Editor
          key={editorKey}
          id={id}
          tinymceScriptSrc={TINYMCE_SCRIPT_PATH}
          scriptLoading={{ async: true }}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={init}
        />
      ) : (
        <MentionsEditor
          key={editorKey}
          id={id}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={init}
          activityType="task"
        />
      )}

      {showAssistModal && (
        <AssistModal
          show
          onHide={() => setShowAssistModal(false)}
          editor={editorRef.current}
          editorType="task"
          leadId={leadId}
          product={GlobalContainer.product()}
        />
      )}
    </div>
  );
};

TaskEditor.propTypes = {
  value: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  leadId: PropTypes.number,
  isCampaignStep: PropTypes.bool,
  spellCheck: PropTypes.bool,
  linksToInsert: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  clearLinksToInsert: PropTypes.func,
  replaceValue: PropTypes.string,
  clearReplaceValue: PropTypes.func,
  enableVariables: PropTypes.bool,
  includeOwnerVariables: PropTypes.bool,
  includeAlertVariables: PropTypes.bool,
};

export default TaskEditor;
